/**
 * Retrieves the user type preference from a user object.
 * @param {Object|null} user - The user object containing id and attributes.
 * @returns {string|null} The user's type preference or null if not found or invalid.
 * @throws {TypeError} If the user parameter is not an object or null.
 */
export const getUserTypePreference = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  const publicData = user?.attributes?.profile?.publicData || {};
  return user?.id?.uuid ? publicData.userTypePreference || publicData.userType || null : null;
};

export const getUserBio = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.bio ?? null : null;
};
export const getUsername = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.publicData?.username ?? null : null;
};

export const getLastLogin = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.publicData?.lastLogin ?? null : null;
};
export const getCoverImage = user => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.attributes?.profile?.publicData?.coverImage ?? null : null;
};

export const getProfileImageUrl = (user, variant = 'square-small') => {
  // Validate input
  if (user !== null && typeof user !== 'object') {
    throw new TypeError('User parameter must be an object or null');
  }

  // Safely access nested properties using optional chaining
  return user?.id?.uuid ? user.profileImage?.attributes?.variants[variant]?.url ?? null : null;
};

/**
 * Returns an array of wishlist items associated with a user's profile.
 * @param {object} currentUser - The user object.
 * @returns {array} An array of wishlist items, or an empty array if input is invalid or wishlist is missing.
 */
export const getWishlist = currentUser => {
  return (
    (currentUser && currentUser.id && currentUser?.attributes?.profile?.protectedData?.wishlist) ||
    []
  );
};

/**
 * Checks if an array is valid (not null, undefined, or empty).
 * @param {array} arr - The array to check.
 * @returns {boolean} True if the array is valid, false otherwise.
 */
export const isArrayLength = arr => {
  // Check if the input parameter is an array and has a length greater than zero.
  return Array.isArray(arr) && (arr.length > 0 ?? false);
};

export const upsertWishlistItem = (currentUser, params) => {
  const rawWishlist = getWishlist(currentUser) || [];

  const { wishlistItem, wishlistName, listings = [] } = params;

  // Find selected listing to get cover image
  const selectedListing =
    isArrayLength(listings) && listings.find(l => l.id?.uuid === wishlistItem);
  const images = selectedListing ? selectedListing.images : [];
  const firstImage = isArrayLength(images)
    ? images[0]?.attributes?.variants['scaled-medium']?.url
    : null;

  // Make a shallow copy of the wishlist (never mutate rawWishlist directly)
  const wishlist = rawWishlist.map(item => ({
    ...item,
    ids: Array.isArray(item.ids) ? [...item.ids] : [],
  }));

  const wishlistIndex = wishlist.findIndex(item => item.name === wishlistName);

  if (wishlistIndex !== -1) {
    // Update existing list immutably
    const existing = wishlist[wishlistIndex];
    const ids = existing.ids || [];

    if (!ids.includes(wishlistItem)) {
      const updatedItem = {
        ...existing,
        ids: [...ids, wishlistItem],
      };

      return wishlist.map((item, i) => (i === wishlistIndex ? updatedItem : item));
    }

    // already in list – return wishlist as-is
    return wishlist;
  }

  // Create new list
  const newList = {
    name: wishlistName,
    ids: [wishlistItem],
    coverImage: firstImage,
  };

  return [...wishlist, newList];
};
